import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import type { Field, Nil } from '@pbt/pbt-ui-components'

import EditableTitle from '~/components/common/EditableTitle'
import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import UserSelect, {
  UserSelectFilterScope,
} from '~/components/common/inputs/UserSelect'
import TransportIcon from '~/components/common/TransportIcon'
import FeatureToggle from '~/constants/featureToggle'
import {
  editConversation,
  setAssigneeForConversation,
} from '~/store/actions/conversations'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Conversation } from '~/types'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import SystemUserSelect from './SystemUserSelect'

const useStyles = makeStyles(
  (theme) => ({
    chatHeader: {
      height: 73,
      padding: theme.spacing(1, 3),
      borderRadius: 2,
      borderBottom: theme.constants.tableBorder,
      backgroundColor: theme.colors.grayWhite,
      boxShadow: theme.constants.listItemShadow,
    },
    transportIcon: {
      color: theme.colors.grayGray3,
      fontSize: '3.6rem',
    },
  }),
  { name: 'ChatHeader' },
)

const ChatHeader = ({
  assigneeId,
  conversation,
}: {
  assigneeId: Field
  conversation: Conversation | Nil
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isContextItem = useIsCurrentContextItem(conversation)
  const isCvcSystemMessageSuppressionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_SYSTEM_MESSAGE_SUPPRESSION),
  )
  const { title, transport } = conversation || {}
  const isSystemMessage =
    isCvcSystemMessageSuppressionEnabled &&
    Boolean(conversation?.isSystemMessage)
  if (!conversation) {
    return (
      <Grid container item alignItems="center" className={classes.chatHeader} />
    )
  }

  const setTitle = (newTitle: string) => {
    dispatch(editConversation(conversation.id, { title: newTitle }))
  }

  return (
    <Grid container item alignItems="center" className={classes.chatHeader}>
      <Grid item>
        <TransportIcon
          classes={{ transportIcon: classes.transportIcon }}
          type={transport}
        />
      </Grid>

      <EditableTitle
        endAdornment={
          <BusinessShareIcon
            businessIds={
              conversation?.businessId ? [conversation.businessId] : []
            }
          />
        }
        readOnly={!isContextItem}
        setTitle={setTitle}
        title={title}
      />

      <Grid item data-testid="user-select">
        {isSystemMessage ? (
          <SystemUserSelect />
        ) : (
          <UserSelect
            disabled={isSystemMessage}
            field={{
              ...assigneeId,
              setValue: (value) => {
                assigneeId.setValue(value)
                dispatch(setAssigneeForConversation(conversation.id, value))
              },
            }}
            filterScope={UserSelectFilterScope.Staff}
            label={assigneeId.label}
            readOnly={!isContextItem}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default ChatHeader
